import { isValidElement } from 'react';
import { Loader2 } from 'lucide-react';
import { GRADIENT_PRIMARY_CLASS } from '../foundation/tokens.js';

/** @type {Record<string, string>} */
const variantClasses = {
  primary: [
    'border-none text-white shadow-none hover:opacity-90',
    GRADIENT_PRIMARY_CLASS,
    'focus-visible:ring-2 focus-visible:ring-[#155DFC]/50 focus-visible:ring-offset-2',
  ].join(' '),
  classicPrimary:
    'border border-transparent bg-[#2173FF] text-white shadow-none hover:bg-[#002F80] focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:bg-[#2173FF] dark:hover:bg-[#04245C]',
  secondary:
    'border border-edge-strong bg-surface text-content shadow-none hover:bg-surface-hover-subtle focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:border-gray-600 dark:bg-gray-900 dark:text-content dark:hover:bg-gray-800 dark:focus-visible:ring-gray-500',
  tertiary:
    'border border-transparent bg-transparent text-content-secondary shadow-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:hover:bg-white/10 dark:focus-visible:ring-gray-500',
  ghost:
    'border border-transparent bg-transparent text-content-secondary shadow-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:hover:bg-white/10 dark:focus-visible:ring-gray-500',
  error:
    'border border-transparent bg-red-400 text-white shadow-none hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2',
  danger:
    'border border-transparent bg-red-600 text-white shadow-none hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:bg-red-500 dark:hover:bg-red-600',
  gradient:
    'border-none bg-[linear-gradient(135deg,#7c5cfc,#5b8def)] text-white shadow-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#7c5cfc]/50 focus-visible:ring-offset-2',
  positive:
    'border border-emerald-600 bg-emerald-600 text-white shadow-none hover:bg-emerald-800 hover:border-emerald-800 focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2',
  purple:
    'border border-purple-500 bg-purple-100 text-purple-900 shadow-none hover:bg-purple-200 hover:border-purple-500 focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 dark:bg-purple-950/40 dark:text-purple-100 dark:border-purple-400',
  grey:
    'border border-gray-600 bg-gray-300 text-gray-700 shadow-none hover:bg-gray-400 hover:border-gray-700 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500 dark:hover:border-gray-400',
};

/**
 * Heights match ann Storybook: sm 25px, md 35px, lg 46px; padding 12/16/20px.
 * `leading-none` avoids default line-height inflating the box.
 */
const sizeClasses = {
  sm: 'h-[25px] gap-1 px-3 text-xs leading-none',
  md: 'h-[35px] gap-2 px-4 text-xs leading-none',
  lg: 'h-[46px] gap-3 px-5 text-lg leading-none',
};

/** Icon-only: padding + 14/14/32px icons → 24 / 40 / 56px hit targets */
const sizeClassesIconOnly = {
  sm: 'h-6 w-6 gap-0 p-1 text-xs leading-none',
  md: 'h-10 w-10 gap-0 p-2 text-xs leading-none',
  lg: 'h-14 w-14 gap-0 p-3 text-lg leading-none',
};

/** Icon sizes when `customIconSize` is omitted: sm 14px, md 14px, lg 32px */
const iconSizeMap = { sm: 14, md: 14, lg: 32 };

function resolveIconSize(customIconSize, sizeKey) {
  if (typeof customIconSize === 'number' && Number.isFinite(customIconSize) && customIconSize > 0) {
    return customIconSize;
  }
  return iconSizeMap[sizeKey];
}

const sizeRadius = {
  sm: 'rounded-lg',
  md: 'rounded-lg',
  lg: 'rounded-2xl',
};

function normalizeSize(size) {
  if (size === 'sm' || size === 'md' || size === 'lg') return size;
  return 'md';
}

function hasRenderableChildren(children) {
  if (children == null || children === false) return false;
  if (Array.isArray(children)) return children.some((c) => c != null && c !== false);
  if (isValidElement(children)) return true;
  if (typeof children === 'string') return children.trim().length > 0;
  return true;
}

/**
 * Button with Tailwind styles aligned to @ann/web-components (maps `variant` to ann `type`).
 *
 * Icons are not bundled: pass Lucide components, e.g. `leftIcon={Image}`. Only `loading` uses internal `Loader2`.
 *
 * @param {object} props
 * @param {'primary'|'classicPrimary'|'secondary'|'tertiary'|'ghost'|'error'|'danger'|'gradient'|'positive'|'purple'|'grey'} [props.variant='primary']
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {import('lucide-react').LucideIcon} [props.leftIcon]
 * @param {import('lucide-react').LucideIcon} [props.rightIcon]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.pill]
 * @param {string} [props.customFontSize] — Tailwind font-size utilities (e.g. `text-[13px]`, `text-xs`). Merged after `size` so it overrides the preset `text-*` from `size`.
 * @param {number} [props.customIconSize] — Lucide icon size in pixels (e.g. `14`). Overrides the icon size implied by `size`.
 * @param {string} [props.customButtonHeight] — Tailwind height utilities (e.g. `!h-[40px]`, `!h-auto`, `min-h-[44px]`). Merged after `size` so it overrides the preset `h-*` / `h-6` from `size`.
 * @param {string} [props.className]
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  loading = false,
  disabled = false,
  pill = false,
  customFontSize,
  customIconSize,
  customButtonHeight,
  className = '',
  type = 'button',
  ...props
}) => {
  const isDisabled = disabled || loading;
  const sz = normalizeSize(size);
  const iconSize = resolveIconSize(customIconSize, sz);

  const showChildren = hasRenderableChildren(children);
  const isIconOnly = !loading && !showChildren && !!(LeftIcon || RightIcon);

  const v = variantClasses[variant] ? variant : 'primary';

  const interactive = !isDisabled
    ? 'transition-colors duration-200 active:scale-[0.98]'
    : 'transition-colors duration-200';

  const shape = pill ? 'rounded-full' : sizeRadius[sz];

  return (
    <button
      type={type}
      className={[
        'inline-flex items-center justify-center font-medium font-sans',
        'focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        interactive,
        shape,
        isIconOnly ? sizeClassesIconOnly[sz] : sizeClasses[sz],
        variantClasses[v],
        typeof customFontSize === 'string' ? customFontSize.trim() : '',
        typeof customButtonHeight === 'string' ? customButtonHeight.trim() : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <Loader2 size={iconSize} className="shrink-0 animate-spin" aria-hidden />
      ) : (
        LeftIcon && <LeftIcon size={iconSize} className="shrink-0" aria-hidden />
      )}
      {showChildren ? children : null}
      {!loading && RightIcon && <RightIcon size={iconSize} className="shrink-0" aria-hidden />}
    </button>
  );
};

export default Button;
